import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useRegisterFilters } from '@hooks';
import { FilterBar, TablePagination } from '@components';
import RegisterTable from '@/shared/components/RegisterTable/RegisterTable';
import { useMfsCatalog } from '@/features/mfs-drafts/useMfsCatalog';
import {
  buildPlanningItems, buildPlanningCoverage, planningSummary, inPlanningScope, planningScopeKey,
  planningFilters, coverageFilters, planningSearch, itemColumns, coverageColumns, sortPlanning,
} from './planning.model';
import PlanningItemDetail from './PlanningItemDetail';
import styles from './InspectionPlanningViews.module.css';

function PlanningTable({ rows, kind, scopeKey, onOpen }) {
  const columns = kind === 'items' ? itemColumns : coverageColumns;
  const filters = useRegisterFilters({
    namespace: kind === 'items' ? 'planItems' : 'planCoverage', items: rows,
    definitions: kind === 'items' ? planningFilters : coverageFilters, getSearchText: planningSearch,
  });
  const [sort, setSort] = useState({ key: 'code', direction: 'asc' });
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ key: '', page: 0 });
  const pageKey = JSON.stringify([scopeKey, filters.query, filters.values, sort, rowsPerPage]);
  const sorted = sortPlanning(filters.filtered, sort, columns);
  const lastPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
  const page = pagination.key === pageKey ? Math.min(pagination.page, lastPage) : 0;
  const visible = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  return <section className={styles.register}>
    <FilterBar filters={filters} noun={kind === 'items' ? 'proposed items' : 'available components'} />
    <div className={styles.tableArea}>
      {visible.length ? <RegisterTable rows={visible} columns={columns} sort={sort} onSortChange={setSort}
        getRowKey={row => row.id} onOpen={onOpen} caption={kind === 'items' ? 'Local draft item previews' : 'Local draft scope inclusion'}
        renderCell={(row, column) => {
          if (column.key === 'code') return <>{row.code}<small>{row.name}</small></>;
          if (column.key === 'location') return <>{row.assetLabel}<small>{row.siteLabel}</small></>;
          if (column.key === 'techniques') return <span className={styles.methods}>{row.techniques.join(' · ') || 'None assigned'}</span>;
          if (column.key === 'planState' || column.key === 'catalogueState') return <span className={styles.badge}>{column.value(row)}</span>;
          return column.value(row) ?? '—';
        }} /> : <div className={styles.empty}><p>{rows.length ? 'No rows match these filters.' : kind === 'items'
          ? 'No proposed items in this selection. Save a local draft or clear the draft/component selection.'
          : 'No available components in this scope.'}</p>
        {rows.length > 0 && <button type="button" className={styles.link} onClick={filters.clear}>Clear filters</button>}</div>}
    </div>
    <TablePagination count={sorted.length} page={page} rowsPerPage={rowsPerPage}
      onPageChange={(_event, next) => setPagination({ key: pageKey, page: next })}
      onRowsPerPageChange={event => setRowsPerPage(Number(event.target.value))} rowsPerPageOptions={[25, 50, 100]} />
  </section>;
}

export default function InspectionPlanningViews({ view, companyId, siteId, assetId, drafts, onEdit, onPreview, onSchedules }) {
  const resource = useMfsCatalog(companyId);
  const [params, setParams] = useSearchParams();
  const scopeKey = planningScopeKey(companyId, siteId, assetId);
  const catalog = !resource.isLoading && !resource.error ? resource.data?.rows ?? null : null;
  const model = useMemo(() => {
    try {
      const items = buildPlanningItems(drafts, companyId, catalog).filter(row => inPlanningScope(row, siteId, assetId));
      const coverage = catalog === null ? null : buildPlanningCoverage(
        catalog.filter(row => inPlanningScope(row, siteId, assetId)), items);
      return { items, coverage, error: null };
    } catch (error) { return { items: [], coverage: null, error: error.message }; }
  }, [drafts, companyId, catalog, siteId, assetId]);
  const draftId = params.get('plan.draft'), componentKey = params.get('plan.component');
  const shownItems = model.items.filter(row => (!draftId || row.draftId === draftId) && (!componentKey || row.key === componentKey));
  const selectedId = params.get('plan.item');
  const selected = params.get('plan.scope') === scopeKey ? model.items.find(row => row.id === selectedId) : null;
  const summary = model.coverage === null ? null : planningSummary(model.coverage);

  function select(item = null) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (item) { next.set('plan.item', item.id); next.set('plan.scope', scopeKey); }
      else { next.delete('plan.item'); next.delete('plan.scope'); }
      return next;
    });
  }
  return <div className={styles.view}>
    <header className={styles.header}><div><span className={styles.eyebrow}>LOCAL DRAFT PREVIEW</span>
      <h2>{view === 'items' ? 'Proposed inspection items' : 'Draft scope coverage'}</h2>
      <p>{view === 'items' ? 'One proposed item per draft and component, with its assigned techniques.' :
        'Which available components are included in your local drafts?'}</p></div>
      <button type="button" className={styles.button} onClick={onSchedules}>Maintenance schedules</button>
    </header>
    <p className={styles.notice}>Planning only. Saved occurrences, completion status, report evidence and anomaly observations are not connected.
      Draft inclusion does not establish inspection coverage or asset condition.</p>
    {resource.error && <div className={styles.notice}><p role="alert">Catalogue check unavailable: {resource.error}</p>
      <button type="button" className={styles.link} onClick={() => resource.reload()}>Retry catalogue</button></div>}
    {resource.isLoading && <p role="status" className={styles.note}>Checking the available component catalogue…</p>}
    {resource.data?.excluded > 0 && <p className={styles.notice}>{resource.data.excluded} company component record(s) were excluded from the available catalogue due to incomplete identity/location or a company mismatch.
      Draft coverage uses only the available catalogue, not all registered records.</p>}
    {model.error ? <p role="alert" className={styles.notice}>{model.error} Review the local drafts before continuing.</p> :
      assetId && !siteId ? <p className={styles.notice}>Choose a site for this asset using the scope selector.</p> :
        view === 'items' ? <>
          <p className={styles.note}>{shownItems.length} proposed item(s) in this selection. Table filters narrow this list.
            Proposed due dates do not represent live overdue work. Catalogue labels show availability by identity, not a revalidation of the whole draft.</p>
          {(draftId || componentKey) && <div className={styles.notice}>
            <p>{draftId ? 'Filtered to one local draft. ' : ''}{componentKey ? 'Filtered to one component.' : ''}</p>
            <button type="button" className={styles.link} onClick={() => onPreview({})}>Show all proposed items in scope</button>
          </div>}
          {selectedId && !selected && <div className={styles.notice}><p>This proposed item is unavailable in the current local drafts or scope.
            Drafts do not survive leaving this page or refreshing.</p><button type="button" className={styles.link} onClick={() => select()}>Dismiss selection</button></div>}
          <PlanningTable key={JSON.stringify(['items', scopeKey, draftId, componentKey])} rows={shownItems} kind="items" scopeKey={scopeKey} onOpen={select} />
          {selected && <PlanningItemDetail key={selected.id} item={selected} onClose={() => select()} onEdit={onEdit} />}
        </> : <>
          <div className={styles.metrics}>
            {[['Available components', summary?.total], ['Included in local drafts', summary?.included],
            ['Not in local drafts', summary?.unplanned], ['In multiple local drafts', summary?.overlap]].map(([label, count]) =>
              <article key={label}><span>{label}</span><strong>{count ?? '—'}</strong></article>)}
          </div>
          <p className={styles.note}>Each component counts once; overlaps are allowed. Counts describe this location scope across all local drafts and do not change with table filters.
            No draft assignment means “not in local drafts”, not “uninspected”.</p>
          {model.coverage !== null && <p className={styles.note}>
            {model.items.filter(item => item.catalogueState === 'Unavailable').length} proposed item(s) refer to components outside the available catalogue and are excluded from these counts.
          </p>}
          {model.coverage !== null ? <PlanningTable key={'coverage:' + scopeKey} rows={model.coverage} kind="coverage" scopeKey={scopeKey}
            onOpen={row => onPreview({ componentKey: row.key })} /> :
            <p className={styles.empty}>Draft coverage is unavailable until the catalogue loads successfully.</p>}
          <section className={styles.future}><h3>Inspection coverage & evidence gaps</h3>
            <p>Not connected. Completion percentages, overdue requirements and missing-report counts require saved inspection records and verified evidence links.</p></section>
        </>}
  </div>;
}